import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { cn } from '@plato/ui/util/classes';
import { captureException } from '@sentry/react';
import { CopyIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, type MouseEvent, type ReactElement } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import type { AiChatCopiedVia } from '../../analytics/types/analytics-events.ts';

import { MessageActionButton, type MessageActionButtonProps } from './message-actions.tsx';
import { Message, MessageContent, type MessageContentProps, type MessageProps } from './message.tsx';
import { ReplyMarkdown, replyCopyText, type PageLinkClick } from './reply-markdown.tsx';

/** Copies `text` to the clipboard and confirms with a toast. */
export function useCopyMessage(): (text: string) => void {
    const { t } = useTranslation('ask-plato');
    const { toast } = useToast();

    return useCallback(
        (text: string) => {
            void (async () => {
                try {
                    await navigator.clipboard.writeText(text);
                    toast({ icon: 'copy', title: t('copied') });
                } catch (error) {
                    captureException(error);
                }
            })();
        },
        [t, toast],
    );
}

export type MessageCopyButtonProps = Pick<MessageActionButtonProps, 'className'> & {
    /** The clipboard text, or a function producing it on click. */
    text: string | (() => string);
    /** Called after the copy is requested, for analytics. Absent on bubbles that are not messages. */
    onCopied?: () => void;
};

/** The copy action as a `MessageActionButton` — in a rating row or a bubble's gutter. */
export function MessageCopyButton({ text, className, onCopied }: MessageCopyButtonProps): ReactElement {
    const { t } = useTranslation('ask-plato');
    const copy = useCopyMessage();

    return (
        <MessageActionButton
            label={t('copy')}
            className={className}
            onClick={() => {
                copy(typeof text === 'function' ? text() : text);
                onCopied?.();
            }}
        >
            <CopyIcon />
        </MessageActionButton>
    );
}

const longPressMs = 500;

export type MessageBubbleProps = Pick<MessageProps, 'align'> &
    Pick<MessageContentProps, 'variant' | 'className'> & {
        text: string;
        /**
         * The text is an assistant reply: Markdown rendered through Lexical,
         * copied as plain text with page links as `label (URL)`. Plain text otherwise.
         */
        markdown?: boolean;
        /** The reply is still streaming into this bubble. */
        streaming?: boolean;
        /** False when a rating row beneath carries the copy action instead. */
        gutterCopy?: boolean;
        /** Called after a copy, with the affordance used. Absent on bubbles that are not messages. */
        onCopied?: (via: AiChatCopiedVia) => void;
        /** Called after a page link in a Markdown bubble was followed. */
        onPageLinkClick?: (click: PageLinkClick) => void;
    };

/** What a bubble puts on the clipboard. */
export function bubbleCopyText(text: string, markdown: boolean): string {
    return markdown ? replyCopyText(text, window.location.origin) : text;
}

/**
 * One text bubble with its copy affordance. On hover devices the whole row is
 * the hover target and a ghost copy button shows in the gutter beside the
 * bubble — right of the assistant, left of the user — the one place that needs
 * no reserved room. On touch, long-pressing the bubble copies it.
 */
export function MessageBubble({
    align = 'start',
    variant,
    className,
    text,
    markdown = false,
    streaming = false,
    gutterCopy = true,
    onCopied,
    onPageLinkClick,
}: MessageBubbleProps): ReactElement {
    const copy = useCopyMessage();
    const copyText = (): string => bubbleCopyText(text, markdown);
    const timerRef = useRef<number | undefined>(undefined);

    const cancelPress = (): void => {
        window.clearTimeout(timerRef.current);
        timerRef.current = undefined;
    };

    useEffect(() => cancelPress, []);

    const pressHandlers = isMobile
        ? {
              onPointerDown: () => {
                  cancelPress();
                  timerRef.current = window.setTimeout(() => {
                      copy(copyText());
                      onCopied?.('long_press');
                  }, longPressMs);
              },
              onPointerUp: cancelPress,
              onPointerLeave: cancelPress,
              onPointerCancel: cancelPress,
              // Long-press must not also open the native selection menu.
              onContextMenu: (event: MouseEvent<HTMLDivElement>) => {
                  event.preventDefault();
              },
          }
        : {};

    return (
        <Message align={align}>
            <MessageContent
                variant={variant}
                className={cn(isMobile && 'select-none', className)}
                {...pressHandlers}
            >
                {markdown ? (
                    <ReplyMarkdown markdown={text} streaming={streaming} onPageLinkClick={onPageLinkClick} />
                ) : (
                    text
                )}
            </MessageContent>
            {gutterCopy && !isMobile && (
                <MessageCopyButton
                    text={copyText}
                    className="opacity-0 group-hover/message:opacity-100 focus-visible:opacity-100"
                    onCopied={() => {
                        onCopied?.('gutter');
                    }}
                />
            )}
        </Message>
    );
}
